import { createContext, useCallback, useContext, useRef, useState } from 'react';
import type { ReactNode } from 'react';

export type PanelMode = 'details' | 'chat';

/** A single chat message. Held in context (not the chat component's local state)
 * so the conversation survives Details↔Chat tab switches and following a
 * citation into a drill and back. */
export interface ReportChatMsg {
  role: 'user' | 'ai';
  content: string;
  followUps?: string[];
  adjacent?: string[];
  pending?: boolean;
}

interface ReportPanelState {
  activeClaimId: string | null;
  activeFilter: string | null;
  mode: PanelMode;
  chatMessages: ReportChatMsg[];
  setChatMessages: (
    updater: ReportChatMsg[] | ((prev: ReportChatMsg[]) => ReportChatMsg[]),
  ) => void;
  chatBusy: boolean; // a chat request is in flight — lives in context so
  setChatBusy: (busy: boolean) => void; // it survives the panel unmounting mid-request
  open: (claimId: string) => void;
  openChat: (claimId?: string | null) => void;
  setMode: (mode: PanelMode) => void;
  close: () => void;
  setFilter: (filter: string | null) => void;
  scrollToSection: (sectionId: string) => void;
}

const Ctx = createContext<ReportPanelState | null>(null);

export function ReportPanelProvider({ children }: { children: ReactNode }) {
  const [activeClaimId, setActiveClaimId] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [mode, setMode] = useState<PanelMode>('details');
  const [chatMessages, setChatMessages] = useState<ReportChatMsg[]>([]);
  const [chatBusy, setChatBusy] = useState(false);
  const savedScrollY = useRef<number>(0);
  // The conversation is ONE persisted thread per report (server-side), continuous
  // across claim scopes — so we do NOT reset on scope change. The scope only
  // changes the "Asking about …" banner + the scope hint sent with the next
  // question. History is loaded from the backend when the panel opens; it's only
  // cleared locally on full close (a fresh open re-loads it).

  const captureScroll = () => {
    if (typeof window !== 'undefined' && activeClaimId === null) {
      savedScrollY.current = window.scrollY;
    }
  };

  const open = useCallback(
    (claimId: string) => {
      // capture scroll position so we can restore it exactly on close (PRD §3.2).
      // one panel at a time: opening a new claim just replaces the active id.
      captureScroll();
      setMode('details');
      setActiveClaimId(claimId);
    },
    [activeClaimId],
  );

  // open the chat panel — optionally scoped to a claim (chips seed from it). A
  // null claim opens an unscoped chat (the floating "Ask" button).
  const openChat = useCallback(
    (claimId: string | null = null) => {
      captureScroll();
      setMode('chat');
      setActiveClaimId(claimId ?? CHAT_UNSCOPED);
    },
    [activeClaimId],
  );

  const close = useCallback(() => {
    setActiveClaimId(null);
    setMode('details');
    setChatMessages([]); // clear locally; a fresh open re-loads persisted history
    setChatBusy(false);
    if (typeof window !== 'undefined') {
      window.scrollTo(0, savedScrollY.current);
    }
  }, []);

  const setFilter = useCallback((filter: string | null) => setActiveFilter(filter), []);

  // close the panel and scroll the report to a section (PRD §10: related SECTION
  // chips jump into the report rather than replacing panel content).
  const scrollToSection = useCallback((sectionId: string) => {
    setActiveClaimId(null);
    setMode('details');
    if (typeof document !== 'undefined') {
      requestAnimationFrame(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, []);

  return (
    <Ctx.Provider
      value={{
        activeClaimId,
        activeFilter,
        mode,
        chatMessages,
        setChatMessages,
        chatBusy,
        setChatBusy,
        open,
        openChat,
        setMode,
        close,
        setFilter,
        scrollToSection,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

/** Sentinel claim id for an unscoped chat (floating Ask button) — the panel is
 * open with no specific claim. */
export const CHAT_UNSCOPED = '__chat__';

export function useReportPanel(): ReportPanelState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useReportPanel must be used within ReportPanelProvider');
  return ctx;
}
